import React, { useEffect, useRef, useState } from "react";
import { api } from "../../../../api/apiClient";
import { useStaticData } from "../../../../context/StaticDataContext";
import { RELATIONSHIP_CATEGORIES } from "../../../../constants/constants";
import { ContactPreference, PersonDetails, PartyRelationshipSummary } from "../../../types/types";
import ContactCard, { getContactValidationErrors, getPersistedContactMethods } from "./ContactCard";
import PartyDetailsCard from "./PartyDetailsCard";
import { notify } from "../../../components-ui/toast";

const createEmptyPerson = (): PersonDetails => ({
  partyId: 0,
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  gender: "",
  partyAddresses: [],
  contactMethods: [],
  contactPreferences: [],
  householdMemberships: [],
  emergencyContact: null,
});

interface Props {
  organisationId: number;
  initialRelationship?: PartyRelationshipSummary | null;
  serviceOptionsUrl?: string;
  relationshipUrl?: string;
  onCancel: () => void;
  onSaved: (
    relationship: PartyRelationshipSummary,
    addAnother: boolean,
  ) => void | Promise<void>;
}

export default function NewOrganisationMemberForm({
  organisationId,
  initialRelationship = null,
  serviceOptionsUrl,
  relationshipUrl,
  onCancel,
  onSaved,
}: Props) {
  const [person, setPerson] = useState<PersonDetails>(createEmptyPerson);
  const [role, setRole] = useState<number | "">(
    initialRelationship?.relationshipTypeId ?? "",
  );
  const [loading, setLoading] = useState(Boolean(initialRelationship));
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState<{ serviceId: number; name: string }[]>([]);
  const [serviceIds, setServiceIds] = useState<number[]>([]);
  const [enrolledIds, setEnrolledIds] = useState<number[]>([]);
  const [loadingServices, setLoadingServices] = useState(Boolean(serviceOptionsUrl));
  const [serviceError, setServiceError] = useState(false);
  const addAnotherRef = useRef(false);
  const { data } = useStaticData();
  const contactTypes = data?.contactTypes ?? [];
  const relationshipTypes = data?.relationshipTypes?.[RELATIONSHIP_CATEGORIES.ORGANISATION] ?? [];
  const isEditing = Boolean(initialRelationship);

  useEffect(() => {
    if (!serviceOptionsUrl) return;
    let cancelled = false;
    setLoadingServices(true);
    setServiceError(false);
    Promise.all([
      api<{ serviceId: number; name: string }[]>(serviceOptionsUrl),
      relationshipUrl ? api<PartyRelationshipSummary>(relationshipUrl) : Promise.resolve(null),
    ]).then(([options, relationship]) => {
      if (cancelled) return;
      const ids = relationship?.serviceIds ?? [];
      setServices(options);
      setServiceIds(ids);
      setEnrolledIds(ids);
    }).catch(() => {
      if (!cancelled) { setServiceError(true); notify.error("Failed to load assigned services"); }
    }).finally(() => { if (!cancelled) setLoadingServices(false); });
    return () => { cancelled = true; };
  }, [serviceOptionsUrl, relationshipUrl]);

  useEffect(() => {
    if (!initialRelationship) {
      setPerson(createEmptyPerson());
      setRole("");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setRole(initialRelationship.relationshipTypeId);

    api<PersonDetails>("/api/person/get-by-id", {
      method: "POST",
      body: initialRelationship.fromPartyId,
    })
      .then((details) => {
        if (!cancelled) setPerson(details);
      })
      .catch((error) => {
        console.error("Failed to load organisation member", error);
        notify.error("Failed to load the member details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialRelationship]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!role) return;
    if (getContactValidationErrors(person.contactMethods, contactTypes).length) {
      notify.error("Please correct invalid email addresses or mobile numbers before saving.");
      return;
    }
    try {
      setSaving(true);
      const savedPerson = await api<PersonDetails>(
        isEditing ? "/api/person/update" : "/api/person/create",
        {
        method: "POST",
        body: { ...person, firstName: person.firstName.trim(), lastName: person.lastName.trim(), dateOfBirth: person.dateOfBirth?.trim() || null, contactMethods: getPersistedContactMethods(person.contactMethods) },
        },
      );
      const addAnother = addAnotherRef.current;
      await onSaved(
        {
          partyRelationshipId:
            initialRelationship?.partyRelationshipId ?? 0,
          fromPartyId: savedPerson.partyId,
          fromPartyName: `${savedPerson.firstName} ${savedPerson.lastName}`.trim(),
          toPartyId: organisationId,
          relationshipTypeId: role,
          ...(serviceOptionsUrl ? { serviceIds } : {}),
          contactPreferences: (
            savedPerson.contactPreferences ?? person.contactPreferences
          ).map((preference) => ({
            contactTypeId: preference.contactTypeId,
            contactTypeCode: preference.contactTypeCode,
            isOptedIn: preference.allowed,
          })),
        },
        addAnother,
      );

      if (addAnother) {
        setPerson(createEmptyPerson());
        setRole("");
        setServiceIds([]);
        setEnrolledIds([]);
      }
    } catch (error) {
      console.error("Failed to save organisation member", error);
      notify.error(
        isEditing
          ? "Failed to update the member"
          : "Failed to create the new person",
      );
    } finally {
      setSaving(false);
    }
  };

  const setPreferences = (contactPreferences: ContactPreference[]) => setPerson((current) => ({ ...current, contactPreferences }));

  if (loading) return <p>Loading member details...</p>;

  return (
    <form className="form" onSubmit={handleSubmit}>
      <PartyDetailsCard details={person} onChange={setPerson} title="Party Details" requireName />
      <ContactCard contacts={person.contactMethods} onChange={(contactMethods) => setPerson((current) => ({ ...current, contactMethods }))} contactPreferences={person.contactPreferences} onPreferenceChange={setPreferences} allowTypeChange />
      <div className="form-group">
        <label className="form-label">Role</label>
        <select className="form-input" value={role} onChange={(event) => setRole(Number(event.target.value))} required>
          <option value="">Select role...</option>
          {relationshipTypes.map((type) => <option key={type.relationshipTypeId} value={type.relationshipTypeId}>{type.name}</option>)}
        </select>
      </div>
      {serviceOptionsUrl && <div className="card">
        <div className="card-title">Assigned services</div>
        <p className="field-hint">The person is enrolled in the selected services. Existing memberships are retained; remove them from the service members screen when required.</p>
        {loadingServices ? <p>Loading services...</p> : serviceError ? <p className="error">Services could not be loaded. Reopen the form to try again.</p> : services.map((service) => <label key={service.serviceId} className="option-item">
          <input type="checkbox" checked={serviceIds.includes(service.serviceId)} disabled={enrolledIds.includes(service.serviceId)} onChange={() => setServiceIds((ids) => ids.includes(service.serviceId) ? ids.filter((id) => id !== service.serviceId) : [...ids, service.serviceId])} />
          {service.name}
        </label>)}
        {!loadingServices && !serviceError && services.length === 0 && <p className="field-hint">No active services are available.</p>}
      </div>}
      <div className="form-group" style={{ marginTop: 24 }}>
        <button
          className="form-submit-button"
          type="submit"
          disabled={saving || loadingServices || serviceError}
          onClick={() => {
            addAnotherRef.current = false;
          }}
        >
          {saving && !addAnotherRef.current ? "Saving..." : "Save"}
        </button>
        {!isEditing && (
          <button
            className="form-submit-button"
            type="submit"
            disabled={saving || loadingServices || serviceError}
            onClick={() => {
              addAnotherRef.current = true;
            }}
            style={{ marginLeft: 12 }}
          >
            {saving && addAnotherRef.current
              ? "Saving..."
              : "Save and add another"}
          </button>
        )}
        <button className="form-submit-button form-submit-button--secondary" type="button" onClick={onCancel} disabled={saving} style={{ marginLeft: 12 }}>Cancel</button>
      </div>
    </form>
  );
}
